// Session Manager for DMM Law
// Tracks user activity and automatically logs out after 10 minutes of inactivity

class SessionManager {
  constructor(store, router) {
    this.store = store
    this.router = router
    this.timeoutDuration = 10 * 60 * 1000 // 10 minutes in milliseconds
    this.activityTimeout = null
    this.isActive = false
    
    // Initialize session tracking
    this.init()
  }

  init() {
    // Only start session tracking if user is authenticated
    if (this.store.getters.isAuthenticated) {
      this.startSessionTracking()
    }
  }

  startSessionTracking() {
    this.isActive = true
    this.resetTimeout()
    
    // Add event listeners for user activity
    this.addActivityListeners()
    
    console.log('Session tracking started - timeout set to 10 minutes')
  }

  stopSessionTracking() {
    this.isActive = false
    
    // Clear the timeout
    if (this.activityTimeout) {
      clearTimeout(this.activityTimeout)
      this.activityTimeout = null
    }
    
    // Remove event listeners
    this.removeActivityListeners()
    
    console.log('Session tracking stopped')
  }

  resetTimeout() {
    // Clear existing timeout
    if (this.activityTimeout) {
      clearTimeout(this.activityTimeout)
    }
    
    // Set new timeout
    this.activityTimeout = setTimeout(() => {
      this.handleSessionTimeout()
    }, this.timeoutDuration)
    
    console.log(`Session timeout reset - will expire in ${this.timeoutDuration / 60000} minutes`)
  }

  handleSessionTimeout() {
    console.log('Session timeout - logging out user due to inactivity')
    
    // Stop tracking
    this.stopSessionTracking()
    
    // Logout user
    this.store.dispatch('logout').then(() => {
      // Redirect to login page
      if (this.router.currentRoute.value.path !== '/') {
        this.router.push('/')
      }
    }).catch(error => {
      console.error('Error during session timeout logout:', error)
      // Force redirect even if logout fails
      if (this.router.currentRoute.value.path !== '/') {
        this.router.push('/')
      }
    })
  }

  addActivityListeners() {
    // Events that indicate user activity
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ]
    
    this.activityHandler = () => {
      if (this.isActive) {
        this.resetTimeout()
      }
    }
    
    // Add listeners to document
    activityEvents.forEach(event => {
      document.addEventListener(event, this.activityHandler, true)
    })
  }

  removeActivityListeners() {
    if (this.activityHandler) {
      const activityEvents = [
        'mousedown',
        'mousemove',
        'keypress',
        'scroll',
        'touchstart',
        'click'
      ]
      
      activityEvents.forEach(event => {
        document.removeEventListener(event, this.activityHandler, true)
      })
      
      this.activityHandler = null
    }
  }

  // Public method to manually reset session (can be called from components)
  resetSession() {
    if (this.isActive) {
      this.resetTimeout()
    }
  }

  // Method to check if session is active
  isSessionActive() {
    return this.isActive
  }

  // Method to get remaining time in minutes
  getRemainingTime() {
    if (!this.activityTimeout) {
      return 0
    }
    
    // This is a rough estimate since we can't directly access the timeout's remaining time
    // In a production environment, you might want to track the actual timeout start time
    return Math.ceil(this.timeoutDuration / (60 * 1000))
  }
}

export default SessionManager
